/**
 * 坐标系转换 —— WGS-84 / GCJ-02 / BD-09。
 *
 * 铁律(docs/08-architecture.md §8.3):
 *   数据库、API、业务逻辑**全部用 WGS-84**。
 *   只在两个出口转换:渲染到国内底图、生成国内导航链接。
 *
 * 任何在中间层转换的代码都会导致难以追查的偏移 bug —— 一个坐标被转了两次,
 * 位置偏出去 600 米,而每一层看起来都是对的。
 */

export interface LatLng {
  lat: number;
  lng: number;
}

const PI = Math.PI;
const X_PI = (PI * 3000) / 180;
/** 克拉索夫斯基椭球长半轴 */
const A = 6378245.0;
/** 第一偏心率平方 */
const EE = 0.006693421622965943;

/**
 * GCJ-02 算法自带的「境外」判定 —— 一个非常粗糙的矩形框。
 *
 * ⚠️ 危险:这个框(经度 72~137.8,纬度 0.83~55.8)**覆盖了整个东南亚**。
 * 吉隆坡、新加坡、曼谷、河内、雅加达全部会被判定为「境内」而遭到偏移。
 *
 * 我们仍然保持和国测局一致(改了反而和高德对不上),但这意味着:
 *
 *   **绝对不能靠坐标判断要不要转换。**
 *
 * 正确做法是由 MapProvider 决定 —— Provider 的选择来自租户配置,不是坐标:
 *   海外租户 → GoogleMapProvider(coordSystem = 'WGS84')→ 根本不调用转换
 *   国内租户 → AmapProvider  (coordSystem = 'GCJ02') → 才调用转换
 *
 * 见 docs/08-architecture.md §8.3。
 */
export function outOfChina({ lat, lng }: LatLng): boolean {
  return lng < 72.004 || lng > 137.8347 || lat < 0.8293 || lat > 55.8271;
}

function transformLat(x: number, y: number): number {
  let ret =
    -100.0 + 2.0 * x + 3.0 * y + 0.2 * y * y + 0.1 * x * y + 0.2 * Math.sqrt(Math.abs(x));
  ret += ((20.0 * Math.sin(6.0 * x * PI) + 20.0 * Math.sin(2.0 * x * PI)) * 2.0) / 3.0;
  ret += ((20.0 * Math.sin(y * PI) + 40.0 * Math.sin((y / 3.0) * PI)) * 2.0) / 3.0;
  ret += ((160.0 * Math.sin((y / 12.0) * PI) + 320 * Math.sin((y * PI) / 30.0)) * 2.0) / 3.0;
  return ret;
}

function transformLng(x: number, y: number): number {
  let ret = 300.0 + x + 2.0 * y + 0.1 * x * x + 0.1 * x * y + 0.1 * Math.sqrt(Math.abs(x));
  ret += ((20.0 * Math.sin(6.0 * x * PI) + 20.0 * Math.sin(2.0 * x * PI)) * 2.0) / 3.0;
  ret += ((20.0 * Math.sin(x * PI) + 40.0 * Math.sin((x / 3.0) * PI)) * 2.0) / 3.0;
  ret += ((150.0 * Math.sin((x / 12.0) * PI) + 300.0 * Math.sin((x / 30.0) * PI)) * 2.0) / 3.0;
  return ret;
}

/** WGS-84 → GCJ-02(高德 / 腾讯 / 谷歌中国) */
export function wgs84ToGcj02(p: LatLng): LatLng {
  if (outOfChina(p)) return { ...p };

  const dLat0 = transformLat(p.lng - 105.0, p.lat - 35.0);
  const dLng0 = transformLng(p.lng - 105.0, p.lat - 35.0);
  const radLat = (p.lat / 180.0) * PI;
  let magic = Math.sin(radLat);
  magic = 1 - EE * magic * magic;
  const sqrtMagic = Math.sqrt(magic);

  const dLat = (dLat0 * 180.0) / (((A * (1 - EE)) / (magic * sqrtMagic)) * PI);
  const dLng = (dLng0 * 180.0) / ((A / sqrtMagic) * Math.cos(radLat) * PI);

  return { lat: p.lat + dLat, lng: p.lng + dLng };
}

/**
 * GCJ-02 → WGS-84,迭代逼近。
 * 加密算法不可逆,用数值迭代收敛,4 次后误差已小于 1 厘米。
 */
export function gcj02ToWgs84(p: LatLng): LatLng {
  if (outOfChina(p)) return { ...p };

  let wgs: LatLng = { ...p };
  for (let i = 0; i < 4; i++) {
    const encoded = wgs84ToGcj02(wgs);
    wgs = {
      lat: wgs.lat + (p.lat - encoded.lat),
      lng: wgs.lng + (p.lng - encoded.lng),
    };
  }
  return wgs;
}

/** GCJ-02 → BD-09(百度) */
export function gcj02ToBd09({ lat, lng }: LatLng): LatLng {
  const z = Math.sqrt(lng * lng + lat * lat) + 0.00002 * Math.sin(lat * X_PI);
  const theta = Math.atan2(lat, lng) + 0.000003 * Math.cos(lng * X_PI);
  return {
    lng: z * Math.cos(theta) + 0.0065,
    lat: z * Math.sin(theta) + 0.006,
  };
}

/** BD-09 → GCJ-02 */
export function bd09ToGcj02({ lat, lng }: LatLng): LatLng {
  const x = lng - 0.0065;
  const y = lat - 0.006;
  const z = Math.sqrt(x * x + y * y) - 0.00002 * Math.sin(y * X_PI);
  const theta = Math.atan2(y, x) - 0.000003 * Math.cos(x * X_PI);
  return {
    lng: z * Math.cos(theta),
    lat: z * Math.sin(theta),
  };
}

export function wgs84ToBd09(p: LatLng): LatLng {
  return gcj02ToBd09(wgs84ToGcj02(p));
}

export function bd09ToWgs84(p: LatLng): LatLng {
  return gcj02ToWgs84(bd09ToGcj02(p));
}

export type CoordSystem = 'WGS84' | 'GCJ02' | 'BD09';

/**
 * 按目标坐标系转换。数据库里永远是 WGS84,这个函数**只在输出边界**用。
 *
 * `to` 必须来自 MapProvider.coordSystem(租户配置驱动),不能自己按坐标猜 ——
 * 原因见 outOfChina 的注释。海外租户传 'WGS84',这里直接原样返回。
 */
export function convertFromWgs84(p: LatLng, to: CoordSystem): LatLng {
  switch (to) {
    case 'WGS84':
      return { ...p };
    case 'GCJ02':
      return wgs84ToGcj02(p);
    case 'BD09':
      return wgs84ToBd09(p);
  }
}
